import type { LeadWithValuation } from '@/types'
import { LEAD_COLUMNS, leadToRow, exportFileName, STATUS_LABELS, formatDateIT } from './common'

export async function exportLeadsPDF(leads: LeadWithValuation[]) {
  const { default: jsPDF } = await import('jspdf')
  const { default: autoTable } = await import('jspdf-autotable')

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' })

  doc.setFontSize(16)
  doc.text('ImmobiliareAI Sanremo - Report Lead', 14, 15)
  doc.setFontSize(10)
  doc.setTextColor(100)
  doc.text(`Generato il ${new Date().toLocaleDateString('it-IT')} - ${leads.length} contatti`, 14, 22)
  doc.setTextColor(0)

  autoTable(doc, {
    startY: 28,
    head: [LEAD_COLUMNS.map(c => c.header)],
    body: leads.map(leadToRow),
    styles: { fontSize: 7, cellPadding: 1.5 },
    headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [249, 250, 251] },
    margin: { left: 8, right: 8 },
  })

  doc.save(exportFileName('leads', 'pdf'))
}

function formatCurrencyPlain(value: number): string {
  if (value == null || isNaN(value)) return '€ 0'
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

export async function exportSingleLeadPDF(lead: LeadWithValuation) {
  const { default: jsPDF } = await import('jspdf')

  const doc = new jsPDF({ unit: 'mm', format: 'a4' })
  const pageWidth = doc.internal.pageSize.getWidth()
  const margin = 16
  const contentWidth = pageWidth - margin * 2
  let y = 20

  // Header band
  doc.setFillColor(37, 99, 235)
  doc.rect(0, 0, pageWidth, 36, 'F')
  doc.setTextColor(255)
  doc.setFontSize(20)
  doc.text('ImmobiliareAI Sanremo', margin, 16)
  doc.setFontSize(11)
  doc.text('Scheda Valutazione Immobiliare', margin, 24)
  doc.setFontSize(9)
  doc.text(`Generata il ${new Date().toLocaleDateString('it-IT')}`, margin, 31)
  doc.setTextColor(0)
  y = 46

  // Section: Dati Contatto
  y = drawSectionTitle(doc, 'DATI CONTATTO', margin, y)
  const name = [lead.first_name, lead.last_name].filter(Boolean).join(' ') || 'Non specificato'
  const contactRows = [
    ['Nome', name],
    ['Email', lead.email || 'Non specificato'],
    ['Telefono', lead.phone || 'Non specificato'],
    ['Stato', STATUS_LABELS[lead.status] || lead.status || 'Non specificato'],
    ['Data richiesta', lead.created_at ? formatDateIT(lead.created_at) : 'Non specificato'],
  ]
  y = drawKeyValueTable(doc, contactRows, margin, y)
  y += 6

  // Section: Dettagli Immobile
  if (lead.valuation) {
    y = drawSectionTitle(doc, 'DETTAGLI IMMOBILE', margin, y)
    const propertyRows = [
      ['Indirizzo', lead.valuation.address || 'Non specificato'],
      ['Tipologia', lead.valuation.property_type || 'Non specificato'],
      ['Superficie', lead.valuation.area ? `${lead.valuation.area} mq` : 'Non specificato'],
      ['Zona', lead.valuation.zone_name_csv || 'Non specificato'],
    ]
    if (lead.valuation.floor) propertyRows.push(['Piano', lead.valuation.floor])
    if (lead.valuation.condition) propertyRows.push(['Condizioni', lead.valuation.condition])
    if (lead.valuation.view) propertyRows.push(['Vista', lead.valuation.view])
    y = drawKeyValueTable(doc, propertyRows, margin, y)
    y += 4

    // Caratteristiche
    const features: string[] = []
    if (lead.valuation.has_elevator) features.push('Ascensore')
    if (lead.valuation.has_pertinenza) features.push('Pertinenza')
    if (lead.valuation.has_good_exposure) features.push('Buona esposizione')
    if (lead.valuation.has_external_spaces) features.push('Spazi esterni')
    if (lead.valuation.has_garage) features.push('Garage')
    if (lead.valuation.has_pool) features.push('Piscina')

    if (features.length > 0) {
      doc.setFontSize(9)
      doc.setTextColor(100)
      doc.text('Caratteristiche:', margin, y)
      y += 5
      doc.setTextColor(0)
      doc.setFontSize(10)
      doc.text(features.join('  •  '), margin, y)
      y += 8
    }
    y += 4

    // Section: Valutazione (highlighted box)
    doc.setFillColor(243, 244, 246)
    doc.roundedRect(margin, y, contentWidth, 42, 3, 3, 'F')

    doc.setFontSize(10)
    doc.setTextColor(100)
    doc.text('VALUTAZIONE STIMATA', margin + 8, y + 10)

    doc.setFontSize(24)
    doc.setTextColor(37, 99, 235)
    doc.text(formatCurrencyPlain(lead.valuation.estimated_value), margin + 8, y + 24)

    doc.setFontSize(10)
    doc.setTextColor(80)
    doc.text(
      `Range: ${formatCurrencyPlain(lead.valuation.min_value)} - ${formatCurrencyPlain(lead.valuation.max_value)}`,
      margin + 8, y + 32
    )
    doc.text(
      `Prezzo/mq: ${formatCurrencyPlain(lead.valuation.price_per_sqm)}`,
      margin + 8, y + 38
    )
    y += 50
  }

  // Notes
  if (lead.notes) {
    y = drawSectionTitle(doc, 'NOTE', margin, y)
    doc.setFontSize(9)
    doc.setTextColor(60)
    const lines = doc.splitTextToSize(lead.notes, contentWidth)
    doc.text(lines, margin, y)
    y += lines.length * 4 + 6
  }

  // Footer
  doc.setFontSize(7)
  doc.setTextColor(150)
  doc.text(
    'Documento generato automaticamente da ImmobiliareAI Sanremo. I valori sono indicativi.',
    margin,
    doc.internal.pageSize.getHeight() - 10
  )

  const fileName = [lead.first_name, lead.last_name].filter(Boolean).join('_') || 'lead'
  doc.save(`valutazione_${fileName}_${new Date().toISOString().split('T')[0]}.pdf`)
}

function drawSectionTitle(doc: import('jspdf').jsPDF, title: string, x: number, y: number): number {
  doc.setFontSize(10)
  doc.setTextColor(37, 99, 235)
  doc.text(title, x, y)
  doc.setDrawColor(37, 99, 235)
  doc.setLineWidth(0.5)
  doc.line(x, y + 1.5, x + 40, y + 1.5)
  doc.setTextColor(0)
  return y + 7
}

function drawKeyValueTable(doc: import('jspdf').jsPDF, rows: string[][], x: number, y: number): number {
  const labelWidth = 40
  doc.setFontSize(9)
  for (const [label, value] of rows) {
    doc.setTextColor(100)
    doc.text(label || '', x, y)
    doc.setTextColor(30)
    doc.text(value || '', x + labelWidth, y)
    y += 6
  }
  return y
}
